/**
 * @file stock-import-tab.tsx
 * @module app/modules/setting/stock-import-tab
 * @description Stock tab for uploading and reconciling bulk stock update
 * excel/csv files. Copied from the deprecated
 * app/modules/settings/stock-update-excel.tsx.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.2.0
 * @version 2.1.2
 */

import React, { useState, useRef } from 'react';
import { Button, Card, Col, Form, Row, Table, Alert } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import APICallService from '../../../api/apiCallService';
import { manageProduct } from '../../../api/apiEndPoints';
import { AllModules } from '../../../utils/constants';
import { success, error } from '../../../Global/toast';

const RESULT_DISPLAY_LIMIT = 100;

/**
 * Stock tab component.
 * @returns {JSX.Element} Stock import tab content.
 */
const StockImportTab = () => {
  const [file, setFile] = useState<File | null>(null);
  const [fileData, setFileData] = useState<any[]>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [matched, setMatched] = useState<any[]>([]);
  const [unmatched, setUnmatched] = useState<any[]>([]);
  const [matchedCount, setMatchedCount] = useState(0);
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [updatedInventoryCount, setUpdatedInventoryCount] = useState(0);
  const [matchedExcelBase64, setMatchedExcelBase64] = useState<string | null>(
    null
  );
  const [unmatchedExcelBase64, setUnmatchedExcelBase64] = useState<
    string | null
  >(null);
  const [apiDone, setApiDone] = useState(false);

  const resetApiResults = () => {
    setApiDone(false);
    setMatched([]);
    setUnmatched([]);
    setMatchedCount(0);
    setUnmatchedCount(0);
    setTotalRows(0);
    setUpdatedInventoryCount(0);
    setMatchedExcelBase64(null);
    setUnmatchedExcelBase64(null);
    setValidationError('');
  };

  // Each entry can list aliases (e.g. dozen / dozens)
  const REQUIRED_HEADERS: string[][] = [
    ['warehouse'],
    ['product'],
    ['carton with dozens'],
    ['carton with pieces'],
    ['Dozen with pieces'],
    ['Pieces'],
  ];

  const normalizeHeader = (h: string) =>
    String(h ?? '')
      .replace(/^\uFEFF/, '') // BOM
      .replace(/[\u00A0\u2007\u202F]/g, ' ') // non-breaking spaces
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const validateColumns = (headers: string[]) => {
    const receivedCols = headers.map(normalizeHeader);
    const missing = REQUIRED_HEADERS.filter(
      (aliases) =>
        !aliases.some((a) => receivedCols.includes(normalizeHeader(a)))
    );
    return {
      valid: missing.length === 0,
      missing: missing.map((aliases) => aliases[0]),
    };
  };

  const downloadBase64Excel = (base64: string, filename: string) => {
    const link = document.createElement('a');
    link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    resetApiResults();

    const selectedFile = e.target.files?.[0];
    if (!selectedFile) {
      setFile(null);
      setFileData([]);
      setColumns([]);
      setIsValid(false);
      return;
    }

    setFile(selectedFile);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      if (!bstr) return;

      try {
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        // Read header row separately — sheet_to_json omits keys for empty cells
        const headerRow = (
          XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' })[0] || []
        ) as unknown[];
        const headers = headerRow
          .map((h) =>
            String(h ?? '')
              .replace(/^\uFEFF/, '')
              .replace(/[\u00A0\u2007\u202F]/g, ' ')
              .replace(/\s+/g, ' ')
              .trim()
          )
          .filter(Boolean);
        const data: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' });

        if (data && data.length > 0) {
          const { valid, missing } = validateColumns(headers);
          if (valid) {
            setColumns(headers);
            setFileData(data.slice(0, RESULT_DISPLAY_LIMIT));
            setIsValid(true);
            setValidationError('');
          } else {
            setIsValid(false);
            setValidationError(
              `File must have columns: Warehouse, Product, Carton with dozens, Carton with pieces, Dozen with pieces, Pieces. Missing: ${missing.join(', ')}`
            );
            setFileData([]);
            setColumns([]);
          }
        } else {
          setIsValid(false);
          setValidationError('The uploaded file is empty.');
          setFileData([]);
          setColumns([]);
        }
      } catch (err) {
        setIsValid(false);
        setValidationError('Error parsing file.');
        setFileData([]);
        setColumns([]);
      }
    };
    reader.readAsBinaryString(selectedFile);
  };

  const handleUpload = async () => {
    if (!file || !isValid) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const apiService = new APICallService(
        manageProduct.bulkStockExcel,
        formData,
        '',
        '',
        '',
        '',
        AllModules
      );

      const response = await apiService.callAPI();

      if (response) {
        success('Excel file processed successfully');
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setFile(null);
        setMatched((response.matched || []).slice(0, RESULT_DISPLAY_LIMIT));
        setUnmatched((response.unmatched || []).slice(0, RESULT_DISPLAY_LIMIT));
        setMatchedCount(
          response.matchedCount ?? (response.matched || []).length
        );
        setUnmatchedCount(
          response.unmatchedCount ?? (response.unmatched || []).length
        );
        setTotalRows(response.totalRows ?? 0);
        setUpdatedInventoryCount(response.updatedInventoryCount ?? 0);
        setMatchedExcelBase64(response.matchedExcelBase64 || null);
        setUnmatchedExcelBase64(response.unmatchedExcelBase64 || null);
        setApiDone(true);
      }
    } catch (err) {
      error('Failed to process excel file');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-5">
      <Row className="align-items-center mb-5">
        <Col md>
          <h1 className="fs-22 fw-bolder">Stock Update Excel</h1>
        </Col>
      </Row>

      <Card className="bg-light border mb-7">
        <Card.Body className="px-7">
          <Row className="mb-4 align-items-end">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fs-16 fw-500">
                  Select Excel / CSV File
                </Form.Label>
                <Form.Control
                  type="file"
                  accept=".xlsx, .csv"
                  onChange={handleFileChange}
                  onClick={(e: any) => {
                    e.target.value = null;
                  }}
                  ref={fileInputRef}
                />
              </Form.Group>
            </Col>
            <Col xs="auto">
              <Button className="uw-btn uw-btn-primary uw-btn-lg"
                variant="primary"
                size="lg"
                onClick={handleUpload}
                disabled={!file || !isValid || loading}
              >
                {!loading && <span className="indicator-label">Upload</span>}
                {loading && (
                  <span
                    className="indicator-progress"
                    style={{ display: 'block' }}
                  >
                    Please wait...
                    <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                  </span>
                )}
              </Button>
            </Col>
          </Row>

          {validationError && <Alert variant="danger">{validationError}</Alert>}

          {isValid && fileData.length > 0 && !apiDone && (
            <div className="mt-5">
              <h4 className="mb-3">File Preview (Showing top 100 rows)</h4>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table striped bordered hover responsive className="bg-white">
                  <thead>
                    <tr>
                      {columns.map((col, idx) => (
                        <th key={idx}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {fileData.map((row, idx) => (
                      <tr key={idx}>
                        {columns.map((col, cIdx) => (
                          <td key={cIdx}>{row[col]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {apiDone && (
            <div className="mt-5">
              <h4 className="text-success mb-3">Upload Results</h4>
              <Alert variant="info" className="mb-5">
                Processed {totalRows.toLocaleString()} rows — matched{' '}
                {matchedCount.toLocaleString()}, unmatched{' '}
                {unmatchedCount.toLocaleString()}, inventory records updated{' '}
                {updatedInventoryCount.toLocaleString()}.
                {(matchedCount > matched.length ||
                  unmatchedCount > unmatched.length) &&
                  ' Tables below show up to 100 rows each. Download Excel for the full lists.'}
              </Alert>

              <div className="mb-5">
                <div className="d-flex align-items-center justify-content-between mb-3 gap-3 flex-wrap">
                  <h5 className="mb-0">
                    Matched Products: {matchedCount.toLocaleString()}
                    {matchedCount > matched.length
                      ? ` (showing ${matched.length})`
                      : ''}
                  </h5>
                  <Button className="uw-btn uw-btn-success uw-btn-sm"
                    variant="success"
                    size="sm"
                    disabled={!matchedExcelBase64 || matchedCount === 0}
                    onClick={() =>
                      matchedExcelBase64 &&
                      downloadBase64Excel(
                        matchedExcelBase64,
                        `matched-stock-update-${Date.now()}.xlsx`
                      )
                    }
                  >
                    Download Matched
                  </Button>
                </div>
                {matched.length > 0 && (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table
                      striped
                      bordered
                      hover
                      responsive
                      size="sm"
                      className="bg-white"
                    >
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Excel Warehouse</th>
                          <th>Carton with dozens</th>
                          <th>Carton with pieces</th>
                          <th>Dozen with pieces</th>
                          <th>Pieces</th>
                        </tr>
                      </thead>
                      <tbody>
                        {matched.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.title}</td>
                            <td>{item.excelWarehouse}</td>
                            <td>{item.CartonWithDozen}</td>
                            <td>{item.CartonWithPieces}</td>
                            <td>{item.DozenWithPieces}</td>
                            <td>{item.Pieces}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>

              <div>
                <div className="d-flex align-items-center justify-content-between mb-3 gap-3 flex-wrap">
                  <h5 className="text-danger mb-0">
                    Unmatched Products: {unmatchedCount.toLocaleString()}
                    {unmatchedCount > unmatched.length
                      ? ` (showing ${unmatched.length})`
                      : ''}
                  </h5>
                  <Button className="uw-btn uw-btn-danger uw-btn-sm"
                    variant="danger"
                    size="sm"
                    disabled={!unmatchedExcelBase64 || unmatchedCount === 0}
                    onClick={() =>
                      unmatchedExcelBase64 &&
                      downloadBase64Excel(
                        unmatchedExcelBase64,
                        `unmatched-stock-update-${Date.now()}.xlsx`
                      )
                    }
                  >
                    Download Unmatched
                  </Button>
                </div>
                {unmatched.length > 0 && (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <Table
                      striped
                      bordered
                      hover
                      responsive
                      size="sm"
                      className="bg-white"
                    >
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>Excel Warehouse</th>
                          <th>Carton with dozens</th>
                          <th>Carton with pieces</th>
                          <th>Dozen with pieces</th>
                          <th>Pieces</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unmatched.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.title}</td>
                            <td>{item.excelWarehouse}</td>
                            <td>{item.CartonWithDozen}</td>
                            <td>{item.CartonWithPieces}</td>
                            <td>{item.DozenWithPieces}</td>
                            <td>{item.Pieces}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default StockImportTab;
